import React, {
  JSX,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  IconCheck,
  IconShield,
  IconShieldTick,
  Link,
  Modal,
  SelectField,
  Tag,
} from 'gatorcomponents';
import withErrorBoundary from '@/utils/errorBoundary';
import { SERVICE_TERMS } from '@/config/urls/supportUrls';
import { useResize } from '@/hooks/useResize';
import BrandByMonarx from '@/components/GatorProtect/BrandByMonarx/BrandByMonarx';
import {
  ProtectionCardActions,
  ProtectionCardButton,
} from '../ProtectionCard/ProtectionCardCompound';

import type { GatorProtectProBillingCycle } from '../../hooks/useGatorProtectProPlan';
import snappyMonarxPath from '../../media/snappy-monarx.svg';
import * as Styles from './UpgradeCycleModal.styles';
import {
  getActivationPriceFallback,
  getCtaLabelDisplayText,
  getCycleKeyForConfirm,
  getCycleKeyForCopy,
  getDiscountForHero,
  getPricePlaceholder,
} from './UpgradeCycleModal.utils';
import type { UpgradeCycleModalProps } from './UpgradeCycleModal.types';

export type {
  UpgradeCycleModalActivationContent,
  UpgradeCycleModalContent,
  UpgradeCycleModalOption,
  UpgradeCycleModalProps,
} from './UpgradeCycleModal.types';

const MOBILE_BREAKPOINT = 425;

const UpgradeCycleOptionLabel = ({ label, tag }: { label: string | JSX.Element; tag?: string }) => (
  <Styles.UpgradeCycleOptionLabel>
    {label}
    {tag && <Tag aria-label={tag} variant="success" size="sm" label={tag} />}
  </Styles.UpgradeCycleOptionLabel>
);

const UpgradeCycleModalInner: React.FC<UpgradeCycleModalProps> = (props) => {
  const {
    upgradeCycleOptions,
    onClose,
    onConfirm,
    loading,
    testId = 'gator-protect-upgrade-cycle-modal',
    showProBadge = true,
  } = props;

  const { width } = useResize();
  const isMobile = width <= MOBILE_BREAKPOINT;

  const [selectedCycleKey, setSelectedCycleKey] = useState<GatorProtectProBillingCycle | null>(null);

  useEffect(() => {
    setSelectedCycleKey((prev) => {
      if (upgradeCycleOptions.length === 0) {
        return null;
      }
      if (prev != null && upgradeCycleOptions.some(o => o.cycleKey === prev)) {
        return prev;
      }
      return upgradeCycleOptions[0].cycleKey;
    });
  }, [upgradeCycleOptions]);

  const selectedOption = useMemo(
    () => upgradeCycleOptions.find(o => o.cycleKey === selectedCycleKey),
    [upgradeCycleOptions, selectedCycleKey],
  );

  const hasPlanSelector = upgradeCycleOptions.length > 0;

  const cycleKeyForCopy = getCycleKeyForCopy(selectedOption, upgradeCycleOptions);
  const discountForHero = getDiscountForHero(selectedOption);
  const pricePlaceholder = getPricePlaceholder(
    selectedOption,
    getActivationPriceFallback(props),
  );

  const paymentCycleOptions = upgradeCycleOptions.map(opt => ({
    value: opt.cycleKey,
    label: (
      <UpgradeCycleOptionLabel label={opt.label} tag={opt.tag} />
    ),
  }));

  /** force remount of SelectField when options appear/change
   * — gatorcomponents SelectField does not sync internal
   * `inputOptions` when `options` prop updates. */
  const selectFieldRemountKey = upgradeCycleOptions.map(o => o.cycleKey).join('|') || 'no-options';

  const selectDisplayValue = selectedOption
    ? <UpgradeCycleOptionLabel label={selectedOption.label} tag={selectedOption.tag} />
    : undefined;

  const cycleKeyForConfirm = getCycleKeyForConfirm(selectedOption);

  const ctaLabelText = getCtaLabelDisplayText(props, cycleKeyForCopy, pricePlaceholder);

  const handleConfirmClick = () => {
    onConfirm(cycleKeyForConfirm);
  };
  const { content, variant } = props;
  const { featureItems } = content;

  return (
    <>
      <Modal
        maxWidth={752}
        maxHeight={1438}
        noContentPadding
        noHeader
        onClose={onClose}
        testId={testId}
      >
        <Styles.ModalCloseIconOverride modalOverlayTestId={testId} />
        <Styles.GradientShell
          data-component="UpgradeCycleModal"
          data-testid={`${testId}__shell`}
        >
          <Styles.HeroBlock>
            <Styles.BrandByMonarxWrapper>
              <BrandByMonarx
                isMobile={isMobile}
                shieldVariant="yellow"
                showProBadge={showProBadge}
                variant="gradient"
                data-testid={`${testId}__brand-row`}
              />
            </Styles.BrandByMonarxWrapper>

            {variant === 'activation' ? (
              <>
                <Styles.HeroTitle>{content.heroTitle}</Styles.HeroTitle>
                <Styles.HeroSubtitle>{content.heroSubtitle}</Styles.HeroSubtitle>
              </>
            ) : (
              <>
                <Styles.HeroTitle>{content.title}</Styles.HeroTitle>
                <Styles.HeroSubtitle>
                  {content.getSubtitleLeading(cycleKeyForCopy)}
                  <strong>{content.getSubtitleEmphasis(cycleKeyForCopy, discountForHero)}</strong>
                </Styles.HeroSubtitle>
              </>
            )}

            <Styles.InnerCard>
              <Styles.FeatureList data-testid={`${testId}__features`}>
                {featureItems.map(feature => (
                  <Styles.FeatureRow key={feature}>
                    <IconCheck size="sm" color="primaryMedium" aria-hidden />
                    <Styles.FeatureText>{feature}</Styles.FeatureText>
                  </Styles.FeatureRow>
                ))}
              </Styles.FeatureList>

              {hasPlanSelector && (
                <Styles.PlanBlock>
                  <Styles.PlanLabel>{content.paymentCycleLabel}</Styles.PlanLabel>
                  <Styles.SelectFieldWrap>
                    <SelectField
                      key={selectFieldRemountKey}
                      disabled={loading || paymentCycleOptions?.length === 0}
                      placeholder={content.selectCyclePlaceholder}
                      size="sm"
                      options={paymentCycleOptions ?? []}
                      value={selectDisplayValue}
                      onClickSelect={({ value }) => {
                        setSelectedCycleKey(value as GatorProtectProBillingCycle);
                      }}
                      testId={`${testId}__payment-cycle`}
                    />
                  </Styles.SelectFieldWrap>
                </Styles.PlanBlock>
              )}

              <Styles.TermsStrip>
                <Styles.TermsLine>
                  <span>{content.termsPrefix}</span>
                  <Link href={SERVICE_TERMS} text={content.termsLink} />
                </Styles.TermsLine>
              </Styles.TermsStrip>

              <Styles.ModalProtectionCtaWrap>
                <ProtectionCardActions actionsTestId={`${testId}__cta-actions`}>
                  <ProtectionCardButton
                    variant="inverse"
                    hasMultipleButtons={false}
                    onClick={handleConfirmClick}
                    testId={`${testId}__cta-activate`}
                    loading={Boolean(loading)}
                  >
                    <IconShield size="md" color="highPure" aria-hidden />
                    {ctaLabelText}
                  </ProtectionCardButton>
                </ProtectionCardActions>
              </Styles.ModalProtectionCtaWrap>

              <Styles.RefundRow>
                <IconShieldTick color="lowPure" size="sm" aria-hidden />
                <Styles.RefundCopy>{content.refundNote}</Styles.RefundCopy>
              </Styles.RefundRow>
            </Styles.InnerCard>
          </Styles.HeroBlock>

          <Styles.MascotWrap aria-hidden>
            <img alt="" src={snappyMonarxPath} />
          </Styles.MascotWrap>
        </Styles.GradientShell>
      </Modal>
    </>
  );
};

export default withErrorBoundary(UpgradeCycleModalInner, 'UpgradeCycleModal');
